import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Modal as SemanticModal, Input, Segment, Grid } from "semantic-ui-react";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { validateTitle } from "utils";
import { addListItem, toggleListItemActive } from "store/actions/tenant";
import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import Modal from "@cayuse/shared-react.components.modal";
import Toggle from "@cayuse/shared-react.components.form.inputs.toggle";

import styles from "./styles.css";


const ModificationsList = ({
	modificationTypes,
	dictionary,
	listName,
	instanceDictionary
}) => {
	const MODAL_ID = "addNewModificationModal";
	const [ isLoading, setIsLoading ] = useState(false);
	const [ doesTitleExist, setDoesTitleExist ] = useState(false);
	const [ isTitleUnique, setIsTitleUnique ] = useState(true);
	const [ addModificationModalOpen, setAddModificationModalOpen ] = useState(false);
	const [ modificationValue, setModificationValue ] = useState("");
	const [ modifications, setModifications ] = useState([]);

	useEffect(()=>{
		setModifications(modificationTypes.modifications);
	}, [ modificationTypes.modifications ]);

	const handleOpen = () => {
		setAddModificationModalOpen(true);
	};

	const handleClose = () => {
		setIsTitleUnique(true);
		setDoesTitleExist(false);
		setModificationValue("");
		setAddModificationModalOpen(false);
	};

	const validate = useCallback((title) => {
		const { trimmedTitle, isTitleNotEmpty, isSameTitleNotPresent } = validateTitle(title, modifications);
		setDoesTitleExist(isTitleNotEmpty);

		if (isTitleNotEmpty) {
			setIsTitleUnique(isSameTitleNotPresent);

			if (isSameTitleNotPresent) {
				setModificationValue(trimmedTitle);
			}
		}
	}, [ modifications ]);

	const handleSave = useCallback(() => {
		if (!isNonEmptyString(modificationValue)) {return;}
		setIsLoading(true);
		const newModification = {
			value: modificationValue,
			active: true,
			hash: modificationValue
		};
		addListItem(newModification, listName, "modifications")
			.finally(()=>{
				setIsLoading(false);
				handleClose();
				window.location.reload();
			});
	}, [modificationValue]);

	const handleCancel = () => {
		setIsTitleUnique(true);
		setDoesTitleExist(false);
		handleClose();
	};

	const modificationRows = useMemo(() => {
		const modificationsList = isNonEmptyArray(modifications) ? [ ...modifications ] : [];
		const activeModifications = modificationsList.filter(modification => modification.active);
		const isOneModificationActive = activeModifications.length === 1;
		const clickHandler = modification => {
			if (!(isOneModificationActive && modification.active)) {
				toggleListItemActive(modification.hash, listName, "modifications");
				setModifications(modificationsList);
			}
		};

		return (Array.isArray(modificationsList)) ? (
			modificationsList.map(modification => {
				const activeAriaText = modification.active ? "active" : "not active";

				return (
					<Grid.Row key={modification.hash}>
						<Grid.Column>{modification.value}</Grid.Column>
						<Grid.Column>
							<Toggle
								aria-label={`${modification.value} is ${activeAriaText}`}
								active={modification.active}
								// TODO: Add component level dictionary
								dictionary={dictionary.components.toggle}
								disabled={isOneModificationActive && modification.active}
								onClick={() => clickHandler(modification)}
							/>
						</Grid.Column>
					</Grid.Row>
				);}))
			: [];
	}, [ modifications ]);

	const addNewModificationButton = (
		<IconButton
			icon="plus"
			labelPosition="left"
			primary
			onClick={handleOpen}
			aria-controls={MODAL_ID}
		>
			{dictionary.addNewReason || "Add New Reason"}
		</ IconButton>
	);

	const addModificationModal = useMemo(() => (
		<Modal closeIcon
			id={MODAL_ID}
			trigger={addNewModificationButton}
			closeOnEscape={true}
			onClose={handleClose}
			open={addModificationModalOpen}
			ariaLabel={dictionary.modalHeader || "Create a new modification."}
		>
			<SemanticModal.Header
				className={`${styles.modalHeader} whiteOnBlue`}
			>
				<h2>{dictionary.modalHeader || "Create a new modification."}</h2>
			</SemanticModal.Header>
			<SemanticModal.Content>
				{
					dictionary.modalContent ||
                    `Enter a title for your new modification and click "Save", 
                    your new modification will be added to the list and set to active.`
				}
				<VerticalSpacing />
				{dictionary.modalGuidance || `Click "cancel" to discard changes and return to list`}
				<VerticalSpacing />
				<Segment className={styles.modalSegment}>
					<Grid columns="equal">
						<Grid.Row className={styles.modalContentRow}>
							<Grid.Column width="7">
								<Input
									type="text"
									aria-label={dictionary.inputAriaLabel || `Modification`}
									autoFocus
									placeholder={dictionary.inputPlaceholder || `Modification...`}
									onChange={({ target: { value } }) => validate(value)}
								/>
							</Grid.Column>
							<Grid.Column verticalAlign="middle" className={styles.validationMessagesWrapper}>
								<span className={styles.validationMessage} hidden={doesTitleExist}>
									{dictionary.errorNoTitle || "Title must not be empty or all whitespace."}
								</span>
								<span className={styles.validationMessage} hidden={isTitleUnique}>
									{dictionary.errorDuplicateTitle || "Title must be unique."}
								</span>
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</Segment>
				<VerticalSpacing />
			</SemanticModal.Content>
			<SemanticModal.Actions>
				<ButtonGroup>
					<Button secondary onClick={handleCancel}>
						{dictionary.cancel || "Cancel"}
					</Button>
					<Button primary
						onClick={handleSave}
						disabled={!doesTitleExist || !isTitleUnique || isLoading}
						busy={isLoading}
						loadingMessage="Save in process"
					>
						{dictionary.saveModification || "Save"}
					</Button>
				</ButtonGroup>
			</SemanticModal.Actions>
		</Modal>
	), [ addNewModificationButton, addModificationModalOpen, doesTitleExist, isTitleUnique, handleSave, handleCancel, validate, modificationValue ]);

	return (
		<div id={styles.modifications}>
			<Segment attached="top" className={styles.headerSegment}>
				<h3>{instanceDictionary.modificationContentHeader || "Modification Reasons"}</h3>
			</Segment>
			<Segment attached="bottom">
				<Segment attached="top" className={styles.gridHeaderContainer}>
					<Grid columns="equal" padded="horizontally">
						<Grid.Row divided>
							<Grid.Column className={`${styles.leftHeaderColumn} ${styles.columnHeader}`}>
								<h4>{dictionary.titleRowHeader || "Reason Title"}</h4>
							</Grid.Column>
							<Grid.Column className={styles.columnHeader}>
								<h4>{dictionary.activeRowHeader || "Active Status"}</h4>
							</Grid.Column>
						</Grid.Row>
					</Grid>
				</Segment>
				<Segment attached="bottom">
					<Grid columns="equal" className={styles.modificationsGrid}>
						{modificationRows}
					</Grid>
				</Segment>
				{addModificationModal}
			</Segment>
		</div>
	);
};

export default ModificationsList;
